import { StatsigClient } from '@statsig/js-client';
import { runStatsigAutoCapture } from '@statsig/web-analytics';
import { watch } from "vue";
import config from '../config';
import { getUserStore } from '../store';
import { useOrganizationStore } from "../store/organization";
import { rg4js } from './raygun';
let client = null;
let initialization = null;
// The userID the client was built for, or last re-identified to.
let identifiedAs = null;

const statsigUser = ({ userID, email }) => ({
    userID,
    email,
    custom: {
        orgId: useOrganizationStore().orgId,
        orgName: useOrganizationStore().orgData?.name
    }
});

// Idempotent: auth.onAuthStateChanged can fire more than once per session
// (token refresh, re-auth) and the startup code calls this both to kick the
// request off early and to await it before mounting. Every call returns the
// same promise, so nobody mounts before the flags are in and the Vue watchers
// are registered once. `identity` names the user when the store does not
// know it yet (it learns _id from /users/me). When the client was started
// for another account (the early kick uses the previous visit's cookie, and
// that session may be gone), the flags are fetched again for the right user
// before the promise resolves, so no flag is ever read for the wrong one.
export function initializeStatsig(identity){
    if (!initialization) {
        identifiedAs = identity?.userID ?? null;
        initialization = initialize(identity);
    } else if (identity?.userID && identity.userID !== identifiedAs) {
        identifiedAs = identity.userID;
        initialization = initialization.then(() => client.updateUserAsync(statsigUser(identity)));
    }
    return initialization;
}

async function initialize(identity){
    // get stores
    const userStore = getUserStore();
    const orgStore = useOrganizationStore();

    watch(() => userStore.profile._id, updateUser);
    watch(() => userStore.profile.email, updateUser);
    watch(() => orgStore.orgId, updateUser);

    // Map the projen-generated VUE_APP_ENV (which mirrors NODE_ENV — e.g. the
    // dev/ephemeral env ships as "development", not "dev") onto the three
    // Statsig tiers. Keep both short and long names so we don't silently fall
    // through to 'production' (which hides gates configured against the dev
    // or staging tier).
    const envMap = {
        dev: 'development',
        development: 'development',
        load: 'staging',
        pr: 'staging',
        staging: 'staging',
        beta: 'production',
        prod: 'production',
        production: 'production',
    };
    const env = envMap[config.VUE_APP_ENV] || 'production';
    client = new StatsigClient(
        config.VUE_APP_STATSIG_API_KEY,
        statsigUser({ userID: identity?.userID ?? userStore.profile._id, email: identity?.email ?? userStore.profile.email }),
        {
            environment: { tier: env },
            networkConfig: {
                initializeUrl: `${window.location.origin}/statsig/initialize`,
                logEventUrl: `${window.location.origin}/statsig/log_event`,
            },
        },
    );

    await client.initializeAsync();
    runStatsigAutoCapture(client);
}

export async function updateUser() {
    // get stores
    const userStore = getUserStore();

    // login() resets the store before filling it again; skip that transient
    // empty profile (the watcher fires again once the id is back).
    if (!client || !userStore.profile._id) return;

    identifiedAs = userStore.profile._id;
    await client.updateUserAsync(statsigUser({ userID: userStore.profile._id, email: userStore.profile.email }));
}

export function checkFlag(featureFlag, defaultOnFailure = false){
    if(config.VUE_APP_ENABLE_ALL_FEATURES === 'on'){
        return true;
    }

    try{
        return client.checkGate(featureFlag);
    }
    catch(e){
        console.error(e)
        rg4js('send', { error: e});
        return defaultOnFailure;
    }
}
